import {useLazyQuery} from "@apollo/client";
import {ForwardedRef, forwardRef} from "react";
import {components, MultiValueGenericProps, OptionProps, SelectInstance} from "react-select";

import {Select} from "@/components/select/Select.tsx";
import {SelectComponent, SelectProps} from "@/components/select/Select.types.ts";
import {TagSwatch} from "@/components/tagSwatch/TagSwatch.tsx";
import {useLoadOptions} from "@/lib/hooks/useLoadOptions.ts";
import {GQL_TAGS, TagsResponse} from "@/lib/queries/checks.ts";
import {ServiceTag} from "@/lib/types/checks.ts";

const TagOption = ({data, children, ...props}: OptionProps<ServiceTag>) => (
  <components.Option {...props} data={data}>
    <span className="d-flex align-items-center gap-2">
      <TagSwatch color={data.color_hex} />
      {children}
    </span>
  </components.Option>
);

const MultiValueTagLabel = ({children, data, ...props}: MultiValueGenericProps<ServiceTag>) => {
  return (
    <components.MultiValueLabel {...props} data={data}>
      <span className="d-flex align-items-center gap-2">
        <TagSwatch color={data.color_hex} />
        {children}
      </span>
    </components.MultiValueLabel>
  );
};

export const TagSelect = forwardRef(
  (
    {options, components: componentOverrides, ...props}: SelectProps<ServiceTag>,
    ref: ForwardedRef<SelectInstance<ServiceTag>>
  ) => {
    const [getTags] = useLazyQuery<TagsResponse>(GQL_TAGS);
    const {options: tags, isLoading} = useLoadOptions<ServiceTag, TagsResponse>({
      options: options,
      fetchOptions: getTags,
      transform: (result) => Array.from(result.data?.tags || []),
    });

    return (
      <Select
        components={{
          MultiValueLabel: MultiValueTagLabel,
          Option: TagOption,
          ...componentOverrides,
        }}
        isLoading={isLoading}
        getOptionLabel={(t: ServiceTag) => t.tag}
        getOptionValue={(t: ServiceTag) => t.tag}
        options={tags}
        {...props}
        ref={ref}
      />
    );
  }
) as SelectComponent;
